import { CHARITY, isTwitchEnabled, overlayIconReplacePath } from "./const.js";

export const charityIsTiltify = (charity) => charity === CHARITY.TILTIFY;
export const charityIsTwitch = (charity) => charity === CHARITY.TWITCH;
export const isYes = (value) => value === "yes";

export function formatCurrencyNumber(num) {
  if (num !== null && num !== undefined && num !== "undefined") {
    return num.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
  }
  return 0;
}

/**
 * Replaces the overlay icon path with the local path for production
 * @param {string} originalPath
 * @returns string
 */
export const getOverlayIcon = (originalPath) => {
  const makeURL = (src) => {
    return new URL(src, import.meta.url).toString();
  };

  if (!overlayIconReplacePath) {
    return makeURL(
      originalPath.replace("/node_modules/@playlive/overlay-config-data/icons", "/icons"),
    );
  }

  return makeURL(originalPath);
};

/**
 * Helper function to get an asset image URL
 * @param src
 * @returns {string} - source URL
 */
export function getAssetImageUrl(src) {
  return new URL(src, import.meta.url).href;
}

/**
 *
 * @param {string} pathname
 * @returns {{type: string, slug: string}}
 */
export function getTypeSlug(pathname) {
  const [type, slug] = pathname.split("/").filter((p) => p.length > 0);
  return { type, slug };
}

export function buildCustomizedURL({
  config,
  urlBase,
  urlSuffix,
  urlSimplified = false,
  addCharityType = isTwitchEnabled,
  addTiltifyCampaign = true,
}) {
  const {
    charityType,
    tiltifyUsername,
    tiltifyUserSlug,
    twitchUsername,
    twitchCampaignID,
    tiltifyCampaignSlug,
    tiltifyIsTeamCampaign,
    tiltifyTeamSlug,
  } = config;
  let id, url;

  if (charityIsTiltify(charityType)) {
    if (tiltifyIsTeamCampaign && tiltifyTeamSlug) {
      id = `+${tiltifyTeamSlug}${addTiltifyCampaign ? `/${tiltifyCampaignSlug}` : ""}`;
    } else if (tiltifyUserSlug || tiltifyUsername) {
      id = `@${tiltifyUserSlug ?? tiltifyUsername}${addTiltifyCampaign ? `/${tiltifyCampaignSlug}` : ""}`;
    }
  } else {
    id = `@${twitchUsername}/${twitchCampaignID}`;
  }

  const baseUri = import.meta.env.VITE_OVERLAY_URI || "https://plo.dev.experience.stjude.org";

  if (urlSimplified) {
    url = new URL(`${urlBase}${urlSuffix ? `/${urlSuffix}` : ""}`, baseUri);
  } else if (addCharityType) {
    url = new URL(`${urlBase}/${charityType}/${id}${urlSuffix ? `/${urlSuffix}` : ""}`, baseUri);
  } else {
    url = new URL(`${urlBase}/${id}${urlSuffix ? `/${urlSuffix}` : ""}`, baseUri);
  }

  const {
    bgColor,
    backgroundOpacity,
    textColor,
    textSize,
    showScroller,
    showChallenges,
    showRewards,
    showDonors,
    showOriginalGoal,
    showGoalAmount,
    scrollerPosition,
    width,
    position,
    verticalType,
    thermColor,
    amount,
    demoMode,
  } = config;
  if (demoMode) {
    url.searchParams.set("demoMode", demoMode);
  }
  if (bgColor) {
    url.searchParams.set("bgColor", bgColor);
  }
  if (backgroundOpacity) {
    url.searchParams.set("backgroundOpacity", backgroundOpacity);
  }
  if (thermColor) {
    url.searchParams.set("thermColor", thermColor);
  }
  if (textColor) {
    url.searchParams.set("textColor", textColor);
  }
  if (textSize) {
    url.searchParams.set("textSize", textSize);
  }
  if (showRewards) {
    url.searchParams.set("showRewards", showRewards);
  }
  if (showDonors) {
    url.searchParams.set("showDonors", showDonors);
  }
  if (showScroller) {
    url.searchParams.set("showScroller", showScroller);
  }
  if (showChallenges) {
    url.searchParams.set("showChallenges", showChallenges);
  }
  if (showOriginalGoal) {
    url.searchParams.set("showOriginalGoal", showOriginalGoal);
  }
  if (showGoalAmount) {
    url.searchParams.set("showGoalAmount", showGoalAmount);
  }
  if (scrollerPosition) {
    url.searchParams.set("scrollerPosition", scrollerPosition);
  }
  if (amount) {
    url.searchParams.set("amount", `${amount}`.replace(".00", ""));
  }

  if (width) {
    url.searchParams.set("width", width);
  }
  if (position) {
    url.searchParams.set("position", position);
  }
  if (verticalType) {
    url.searchParams.set("type", verticalType);
  }

  // Event List Specific
  const { color, orientation, align, listAmount } = config;
  if (color) {
    url.searchParams.set("color", color);
  }
  if (orientation) {
    url.searchParams.set("orientation", orientation);
  }
  if (align) {
    url.searchParams.set("align", align);
  }
  if (listAmount) {
    url.searchParams.set("listAmount", listAmount);
  }

  // Leaderboard Specific
  const { leaderboardColor, leaderDonors, timeType } = config;

  if (leaderboardColor) {
    url.searchParams.set("bgColor", leaderboardColor);
  }
  if (leaderDonors) {
    url.searchParams.set("topDonors", leaderDonors);
  }
  if (timeType) {
    url.searchParams.set("timeType", timeType);
  }

  // Rewards Specific
  const {
    rewardsTheme,
    // rotationTimer,
    rewardsBackgroundColor,
    highlightFeaturedRewards,
    showQuantityClaimed,
    showAllRewards,
  } = config;

  if (rewardsTheme) {
    url.searchParams.set("theme", rewardsTheme);
  }
  // if (rotationTimer) {
  //   switch (rotationTimer) {
  //     case 'FIVE_MINUTES':
  //       url.searchParams.set('rotationTimer', '5');
  //       break;
  //     case 'TEN_MINUTES':
  //       url.searchParams.set('rotationTimer', '10');
  //       break;
  //     case 'FIFTEEN_MINUTES':
  //       url.searchParams.set('rotationTimer', '15');
  //       break;
  //     default:
  //       url.searchParams.set('rotationTimer', '0');
  //       break;
  //   }
  // }

  if (rewardsBackgroundColor) {
    url.searchParams.set("bgColor", rewardsBackgroundColor);
  }
  if (highlightFeaturedRewards) {
    url.searchParams.set("highlightFeaturedRewards", highlightFeaturedRewards);
  }
  if (showQuantityClaimed) {
    url.searchParams.set("showQuantityClaimed", showQuantityClaimed);
  }
  if (showAllRewards) {
    url.searchParams.set("showAllRewards", showAllRewards);
  }

  // Previous Totals Specific

  const {
    previousTotalsOverlayTheme,
    previousTotalsOverlayBackgroundColor,
    previousTotalsOverlayMaxVisible,
  } = config;
  if (previousTotalsOverlayTheme) {
    url.searchParams.set("theme", previousTotalsOverlayTheme);
  }
  if (previousTotalsOverlayBackgroundColor) {
    url.searchParams.set("bgColor", previousTotalsOverlayBackgroundColor);
  }
  if (previousTotalsOverlayMaxVisible) {
    url.searchParams.set("maxVisible", previousTotalsOverlayMaxVisible);
  }

  // Timers
  const { displayTimer } = config;
  if (displayTimer) {
    url.searchParams.set("displayTimer", displayTimer);
  }

  // Subathon timer specific
  const { intensity, glowEffect, glowEffectMatches } = config;

  if (intensity) {
    url.searchParams.set("intensity", intensity);
  }
  if (glowEffect) {
    url.searchParams.set("glowEffect", glowEffect);
  }
  if (glowEffectMatches) {
    url.searchParams.set("glowEffectMatches", glowEffectMatches);
  }

  // Polls specific
  const { pollsBackgroundColor, pollsType, pollsShowDollars, pollsShowPercentage } = config;

  if (pollsBackgroundColor) {
    url.searchParams.set("bgColor", pollsBackgroundColor);
  }
  if (pollsType) {
    url.searchParams.set("type", pollsType);
  }
  if (pollsShowDollars) {
    url.searchParams.set("showDollars", pollsShowDollars);
  }
  if (pollsShowPercentage) {
    url.searchParams.set("showPercentage", pollsShowPercentage);
  }

  // Alerts specific
  const { incentives, minDono, minAudio, sound, volume } = config;

  if (minDono) {
    url.searchParams.set("min-donation", minDono);
  }
  if (minAudio) {
    url.searchParams.set("minAudio", minAudio);
  }
  if (volume) {
    url.searchParams.set("volume", volume);
  }
  if (sound) {
    url.searchParams.set("sound", sound);
  }
  if (incentives) {
    url.searchParams.set("incentives", incentives);
  }

  // TTS specific
  const { pitch, selectedVoice, pollID, usePoll, pollMapping, autoStart } = config;
  if (pitch) {
    url.searchParams.set("pitch", pitch);
  }
  if (autoStart) {
    url.searchParams.set("autoStart", autoStart);
  }

  if (isYes(usePoll) && pollID && pollID.length > 0 && pollMapping) {
    url.searchParams.set("usePoll", usePoll);
    url.searchParams.set("pollMapping", pollMapping);
    url.searchParams.set("pollID", pollID);
  }

  if (selectedVoice && (!usePoll || !isYes(usePoll))) {
    url.searchParams.set("selectedVoice", selectedVoice);
  }

  // Milestone specific
  const { visual } = config;
  if (visual) {
    url.searchParams.set("visual", visual);
  }

  // Total specific
  const { eventID } = config;
  if (eventID) {
    url.searchParams.set("eventID", eventID);
  }

  const { startTime, endTime } = config;
  if (startTime) {
    url.searchParams.set("startTime", startTime);
  }
  if (endTime) {
    url.searchParams.set("endTime", endTime);
  }

  const {
    donorListBackgroundColor,
    donorListDisplayIncentives,
    donorListOrientation,
    donorListAlign,
    donorListAmount,
  } = config;

  if (donorListBackgroundColor) {
    url.searchParams.set("bgColor", donorListBackgroundColor);
  }
  if (donorListDisplayIncentives) {
    url.searchParams.set("displayIncentives", donorListDisplayIncentives);
  }
  if (donorListOrientation) {
    url.searchParams.set("orientation", donorListOrientation);
  }
  if (donorListAlign) {
    url.searchParams.set("alignment", donorListAlign);
  }
  if (donorListAmount) {
    url.searchParams.set("showDonors", donorListAmount);
  }

  const { minDonation, highlight, donorMin } = config;
  if (minDonation) {
    url.searchParams.set("minDonation", minDonation);
  }
  if (highlight) {
    url.searchParams.set("highlight", highlight);
  }
  if (donorMin) {
    url.searchParams.set("donorMin", donorMin);
  }

  const { giftsThatGiveAmount } = config;
  if (giftsThatGiveAmount) {
    url.searchParams.set("amount", giftsThatGiveAmount);
  }

  // Ball Pit
  const {
    minAmount,
    maxAmount,
    maxBalls,
    maxScale,
    randomColors,
    scaleSize,
    showNames,
    startSize,
    clampVelocity,
    clampAngularSpeed,
    explosionForceMagnitude,
  } = config;

  if (minAmount !== undefined) {
    url.searchParams.set("minAmount", minAmount);
  }
  if (maxAmount !== undefined) {
    url.searchParams.set("maxAmount", maxAmount);
  }
  if (maxBalls !== undefined) {
    url.searchParams.set("maxBalls", maxBalls);
  }
  if (maxScale !== undefined) {
    url.searchParams.set("maxScale", maxScale);
  }
  if (clampVelocity !== undefined && Number(clampVelocity) !== 12) {
    url.searchParams.set("clampVelocity", clampVelocity);
  }
  if (randomColors !== undefined) {
    url.searchParams.set("randomColors", randomColors);
  }
  if (scaleSize !== undefined) {
    url.searchParams.set("scaleSize", scaleSize);
  }
  if (showNames !== undefined) {
    url.searchParams.set("showNames", showNames);
  }
  if (startSize !== undefined) {
    url.searchParams.set("startSize", startSize);
  }
  if (clampAngularSpeed !== undefined && Number(clampAngularSpeed) !== 0.03) {
    url.searchParams.set("clampAngularSpeed", clampAngularSpeed);
  }
  if (explosionForceMagnitude !== undefined && Number(explosionForceMagnitude) !== 0.025) {
    url.searchParams.set("explosionForceMagnitude", explosionForceMagnitude);
  }

  const { ballType } = config;

  if (ballType !== undefined) {
    url.searchParams.set("ballType", ballType);
  }

  const { debugMode, showAllDonations } = config;

  if (debugMode !== undefined && debugMode === "yes") {
    url.searchParams.set("debugMode", "yes");
  }

  if (showAllDonations !== undefined && showAllDonations === "yes") {
    url.searchParams.set("showAllDonations", "yes");
  }

  const { allowNegative, alignment } = config;

  if (allowNegative !== undefined) {
    url.searchParams.set("allowNegative", allowNegative);
  }

  if (alignment !== undefined) {
    url.searchParams.set("alignment", alignment);
  }

  const { type } = config;

  if (type) {
    url.searchParams.set("type", type);
  }

  const {
    donationTrainTheme,
    donationTrainPosition,
    donationTrainMaxVisible,
    donationTrainColor,
    showActivationMessage,
  } = config;

  if (donationTrainColor) {
    url.searchParams.set("bgColor", donationTrainColor);
  }
  if (donationTrainTheme) {
    url.searchParams.set("theme", donationTrainTheme);
  }
  if (donationTrainPosition) {
    url.searchParams.set("position", donationTrainPosition);
  }
  if (donationTrainMaxVisible) {
    url.searchParams.set("maxTrainsVisible", donationTrainMaxVisible);
  }
  if (showActivationMessage) {
    url.searchParams.set("showActivationMessage", showActivationMessage);
  }

  if ("ccamMode" in config && config.ccamMode === "yes") {
    url.searchParams.delete("bgColor");
    url.searchParams.set("ccamMode", "yes");
  }

  if ("ccamFlagVerticalPosition" in config && config.ccamFlagVerticalPosition !== "bottom") {
    url.searchParams.set("ccamFlagVerticalPosition", config.ccamFlagVerticalPosition);
  }

  if ("ccamFlagHorizontalPosition" in config && config.ccamFlagHorizontalPosition !== "right") {
    url.searchParams.set("ccamFlagHorizontalPosition", config.ccamFlagHorizontalPosition);
  }

  // ws or http mode

  return url.href;
}

export function formatColorName(colorName) {
  const formatted = colorName === "default-red" ? "red" : `${colorName.replace(/-/g, " ")}`;
  return formatted.replace(/(^|\s)\S/g, (t) => t.toUpperCase());
}

export function calculateGiftsThatGiveGoal(rawGoal) {
  const goal = parseInt(`${rawGoal}`.split(".")[0], 10);

  if (goal < 100) {
    return 0;
  } else if (goal < 500) {
    return 100;
  } else if (goal < 1000) {
    return 500;
  } else if (goal < 1500) {
    return 1000;
  } else if (goal < 2000) {
    return 1500;
  } else if (goal < 3000) {
    return 2000;
  } else if (goal < 5000) {
    return 3000;
  } else if (goal < 7500) {
    return 5000;
  } else if (goal < 10000) {
    return 7500;
  } else if (goal < 20000) {
    return 10000;
  } else if (goal < 30000) {
    return 20000;
  } else if (goal < 40000) {
    return 30000;
  } else if (goal < 50000) {
    return 40000;
  } else if (goal < 60000) {
    return 50000;
  } else if (goal < 70000) {
    return 60000;
  } else if (goal < 75000) {
    return 70000;
  } else if (goal < 80000) {
    return 75000;
  } else if (goal < 90000) {
    return 80000;
  } else if (goal < 100000) {
    return 90000;
  } else {
    return 100000;
  }
}
